/**
 * Algorhythm Integration Types
 *
 * These types define the required metadata fields for Algorhythm API integration.
 * All assets (across all layers) must have these 5 fields to be sent to Algorhythm.
 *
 * Based on: /docs/code-review/metadata-analysis/CANONICAL_PATTERNS_132_ASSETS_ALIGNED.md
 * Backend Alignment: /Users/ajaymadhok/nna-registry-service/docs/code-review/metadata-analysis/
 */

import {
  ALGORHYTHM_METADATA,
  validateArrayLength,
  validateArrayItems,
} from '../constants/apiMetadataConstraints';

/**
 * Algorhythm Required Fields (ALL LAYERS: S, L, G, M, W, C)
 *
 * These 5 fields are REQUIRED for all assets to integrate with Algorhythm API.
 * Without these fields, assets cannot be sent to Algorhythm for matching.
 */
export interface AlgorhythmMetadata {
  /** Performance context (multi-select) - min 1 value required */
  performanceContext: string[];

  /** Target audience (multi-select) - min 1 value required */
  targetAudience: string[];

  /** Cultural context (multi-select) - min 1 value required */
  culturalContext: string[];

  /** Musical style (multi-select) - min 1 value required */
  musicalStyle: string[];

  /** Energy level (single select) - exactly 1 value required */
  energyLevel: 'low' | 'medium' | 'high' | 'extreme';
}

/**
 * Aggregated Metadata for Composite Assets (C layer only)
 *
 * This metadata is calculated by aggregating metadata from 5 component assets.
 * Backend computes this when Composite is created/updated.
 * Frontend can preview this in real-time before saving.
 */
export interface AggregatedMetadata {
  /** Overall synergy score (0-100) - how well components work together */
  synergyScore: number;

  /** Visual cohesion score (0-1) - color/style alignment between Look + World */
  visualCohesion: number;

  /** Cultural alignment score (0-1) - cultural consistency across components */
  culturalAlignment: number;

  /** Energy balance score (0-1) - compatible energy levels (Song + Star + Move) */
  energyBalance: number;

  /** Audience match score (0-1) - target audience overlap */
  audienceMatch: number;

  /** Thematic coherence score (0-1) - mood alignment (Song + World) */
  thematicCoherence: number;

  /** Dominant colors from Look + World components */
  dominantColors: string[];

  /** Dominant mood from all components */
  dominantMood: string[];
}

/**
 * Allowed values for Algorhythm fields (from Algorhythm API specification)
 *
 * These enums match the backend validation and Algorhythm API requirements.
 * Frontend must validate against these values before submission.
 */
export const ALGORHYTHM_ENUMS = {
  performanceContext: [
    'studio',
    'concert',
    'dance_stage',
    'outdoor',
    'virtual'
  ] as const,

  targetAudience: [
    'children',
    'teens',
    'young_adults',
    'adults',
    'all_ages'
  ] as const,

  culturalContext: [
    'western',
    'k_pop',
    'j_pop',
    'latin',
    'afrobeat',
    'bollywood'
  ] as const,

  musicalStyle: [
    'pop',
    'hip_hop',
    'rock',
    'electronic',
    'ballad',
    'r&b'
  ] as const,

  energyLevel: [
    'low',
    'medium',
    'high',
    'extreme'
  ] as const,
};

/**
 * Human-readable labels for Algorhythm enum values
 * Used in UI dropdowns and multi-selects
 */
export const ALGORHYTHM_LABELS = {
  performanceContext: {
    studio: 'Studio Performance',
    concert: 'Concert/Live Show',
    dance_stage: 'Dance Stage',
    outdoor: 'Outdoor Performance',
    virtual: 'Virtual/Online Performance',
  },

  targetAudience: {
    children: 'Children (0-12)',
    teens: 'Teenagers (13-17)',
    young_adults: 'Young Adults (18-30)',
    adults: 'Adults (30+)',
    all_ages: 'All Ages',
  },

  culturalContext: {
    western: 'Western',
    k_pop: 'K-Pop (Korean)',
    j_pop: 'J-Pop (Japanese)',
    latin: 'Latin',
    afrobeat: 'Afrobeat',
    bollywood: 'Bollywood (Indian)',
  },

  musicalStyle: {
    pop: 'Pop',
    hip_hop: 'Hip Hop',
    rock: 'Rock',
    electronic: 'Electronic',
    ballad: 'Ballad',
    'r&b': 'R&B',
  },

  energyLevel: {
    low: 'Low Energy (Calm, Relaxed)',
    medium: 'Medium Energy (Moderate)',
    high: 'High Energy (Energetic, Upbeat)',
    extreme: 'Extreme Energy (Intense, Explosive)',
  },
};

/**
 * AI suggestions from backend (optional)
 * Backend analyzes creator description and suggests Algorhythm field values
 */
export interface AlgorhythmSuggestions {
  performanceContext?: string[];
  targetAudience?: string[];
  culturalContext?: string[];
  musicalStyle?: string[];
  energyLevel?: 'low' | 'medium' | 'high' | 'extreme';
  confidence?: number; // 0-1, how confident AI is in suggestions
}

/**
 * Validation result for Algorhythm metadata
 */
export interface AlgorhythmValidation {
  isValid: boolean;
  errors: string[];
  warnings?: string[];
}

/**
 * Helper function to validate Algorhythm metadata
 * Returns validation result with specific error messages.
 * Aligned with OpenAPI AlgorhythmMetadataRequest: arrays min 1 max 50, items maxLength 255.
 */
export function validateAlgorhythmMetadata(
  metadata: Partial<AlgorhythmMetadata>
): AlgorhythmValidation {
  const errors: string[] = [];
  const arrayOpts = {
    minItems: ALGORHYTHM_METADATA.arrayMinItems,
    maxItems: ALGORHYTHM_METADATA.arrayMaxItems,
  };

  // Check all required array fields (OpenAPI: min 1 max 50)
  const arrayFields: { key: keyof AlgorhythmMetadata; label: string }[] = [
    { key: 'performanceContext', label: 'Performance Context' },
    { key: 'targetAudience', label: 'Target Audience' },
    { key: 'culturalContext', label: 'Cultural Context' },
    { key: 'musicalStyle', label: 'Musical Style' },
  ];
  for (const { key, label } of arrayFields) {
    const value = metadata[key];
    if (!Array.isArray(value) || value.length === 0) {
      errors.push(`${label} is required (select at least 1)`);
    } else {
      const msg = validateArrayLength(value, arrayOpts, label);
      if (msg) errors.push(msg);
      else {
        const itemMsg = validateArrayItems(
          value,
          ALGORHYTHM_METADATA.stringMaxLength,
          label
        );
        if (itemMsg) errors.push(itemMsg);
      }
    }
  }

  if (!metadata.energyLevel) {
    errors.push('Energy Level is required (select exactly 1)');
  }

  // Validate energyLevel is one of allowed values
  if (metadata.energyLevel && !ALGORHYTHM_ENUMS.energyLevel.includes(metadata.energyLevel as any)) {
    errors.push('Energy Level must be: low, medium, high, or extreme');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Helper function to check if Algorhythm metadata is complete
 * Used for form validation before submission
 */
export function isAlgorhythmMetadataComplete(
  metadata: Partial<AlgorhythmMetadata>
): boolean {
  return validateAlgorhythmMetadata(metadata).isValid;
}

/**
 * Type guard to check if metadata is complete AlgorhythmMetadata
 */
export function isCompleteAlgorhythmMetadata(
  metadata: Partial<AlgorhythmMetadata>
): metadata is AlgorhythmMetadata {
  return isAlgorhythmMetadataComplete(metadata);
}
